import { Helmet } from 'react-helmet-async';
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const AuditPage = () => {
    const [publicAudit, setPublicAudit] = useState({ status: 'pending', columns: [], sensitiveFound: [] });
    const [privateAudit, setPrivateAudit] = useState({ status: 'pending', data: null, error: null });
    const [missingColumns, setMissingColumns] = useState([]);

    const REQUIRED_FRONTEND_COLUMNS = [
        'id_offre', 'intitule_poste', 'entreprise', 'pays', 'ville',
        'duree', 'date_publication', 'offre_complete', 'specialization_label_fr',
        'parent_label_fr',
        'langues_requises', 'sidebar_summary', 'company_html',
        'mission_html', 'profile_html', 'url_live'
    ];

    const SENSITIVE_COLUMNS = ['email', 'contact_email', 'telephone', 'nom_contact', 'lien_candidature'];

    useEffect(() => {
        runAudit();
    }, []);

    const runAudit = async () => {
        // 1. Audit Public Table
        try {
            const { data, error } = await supabase
                .from('offres_vie_bf_public')
                .select('*')
                .limit(1);

            if (error) {
                setPublicAudit({ status: 'error', error: error.message });
            } else if (data && data.length > 0) {
                const row = data[0];
                const foundColumns = Object.keys(row);

                // Check Missing
                const missing = REQUIRED_FRONTEND_COLUMNS.filter(col => !foundColumns.includes(col));
                setMissingColumns(missing);

                // Check Sensitive
                const sensitive = SENSITIVE_COLUMNS.filter(col => foundColumns.includes(col));

                setPublicAudit({
                    status: 'success',
                    columns: foundColumns,
                    sensitiveFound: sensitive
                });
            } else {
                setPublicAudit({ status: 'empty', error: 'No data found in public table' });
            }
        } catch (e) {
            setPublicAudit({ status: 'crash', error: e.message });
        }

        // 2. Audit Private Table (Security Check)
        try {
            const { data, error } = await supabase
                .from('offres_vie_bf_private')
                .select('*')
                .limit(1);

            if (data && data.length > 0) {
                setPrivateAudit({ status: 'risk', data: 'ABLE TO READ DATA (RLS MISSING?)', count: data.length });
            } else if (error) {
                setPrivateAudit({ status: 'secure', error: error.message }); // Error is good here usually (e.g. 401/403)
            } else {
                setPrivateAudit({ status: 'secure', data: 'No data returned (RLS likely active)' });
            }
        } catch (e) {
            setPrivateAudit({ status: 'error', error: e.message });
        }
    };

    return (
        <div className="p-10 max-w-4xl mx-auto font-sans text-slate-800">
            <Helmet><meta name='robots' content='noindex, nofollow' /></Helmet>
            <h1 className="text-3xl font-bold mb-6">Database Audit Report</h1>

            {/* Public Table Report */}
            <div className="mb-8 p-6 bg-white rounded-xl shadow border">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    1. Public Table Audit (`offre_vie_bf_public`)
                    {publicAudit.status === 'success' && missingColumns.length === 0 && publicAudit.sensitiveFound.length === 0 ?
                        <span className="text-green-600">✅ PASS</span> : <span className="text-red-600">⚠️ ISSUES</span>}
                </h2>

                {publicAudit.error && <div className="text-red-500 font-bold">Error: {publicAudit.error}</div>}

                {publicAudit.status === 'success' && (
                    <div className="space-y-4">
                        <div>
                            <h3 className="font-semibold text-slate-600">Missing Frontend Columns:</h3>
                            {missingColumns.length > 0 ? (
                                <ul className="list-disc pl-5 text-red-600 font-mono text-sm mt-1">
                                    {missingColumns.map(c => <li key={c}>{c}</li>)}
                                </ul>
                            ) : (
                                <p className="text-green-600">None. All required columns present.</p>
                            )}
                        </div>

                        <div>
                            <h3 className="font-semibold text-slate-600">Sensitive Columns Found (Should be removed):</h3>
                            {publicAudit.sensitiveFound.length > 0 ? (
                                <ul className="list-disc pl-5 text-red-600 font-mono text-sm mt-1">
                                    {publicAudit.sensitiveFound.map(c => <li key={c}>{c}</li>)}
                                </ul>
                            ) : (
                                <p className="text-green-600">None found. Good.</p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Private Table Report */}
            <div className="mb-8 p-6 bg-white rounded-xl shadow border">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    2. Security Check (`offre_vie_bf_private`)
                    {privateAudit.status === 'secure' ? <span className="text-green-600">✅ SECURE</span> : <span className="text-red-600">⚠️ RISK</span>}
                </h2>

                <p>Status: <span className="font-mono font-bold">{privateAudit.status}</span></p>
                {privateAudit.data && <p className="mt-2 text-sm">{privateAudit.data}</p>}
                {privateAudit.error && <p className="mt-2 text-sm text-gray-500">Error (Expected): {privateAudit.error}</p>}

                <div className="mt-4 p-4 bg-yellow-50 text-yellow-800 text-sm rounded">
                    <strong>Note:</strong> If you see "ABLE TO READ DATA", it means a public visitor could theoretically extract your contact data. RLS is required.
                </div>
            </div>
        </div>
    );
};

export default AuditPage;
